/**
 * safeRedirect — post-verification redirect resolver (Post #4963, q-101ec46edabd).
 *
 * Why this exists: auth-api's verify-email response carries the `next_url`
 * the signup remembered. For external products (3D Presenter) that is an
 * ABSOLUTE https URL. Handing it to React-Router's `navigate()` treats it as
 * an SPA-relative path — the router normalized `https://presenter…` into
 * `/verify/<token>/https:/presenter…` and the user landed on a blank page
 * (measured by 3DPresenter-Codex in production, 2026-09-10).
 *
 * Contract (mirrors auth-api's `next_url_validator`, both ends enforce):
 *   - internal path (`/…`, not `//…`)            → router navigation
 *   - absolute URL whose parsed ORIGIN is on the
 *     allowlist (or is this instance's own origin) → full navigation
 *     (`window.location.replace`) — never through the router
 *   - everything else                             → fallback to "/"
 *
 * Rejected before any decision: control characters (C0, DEL, U+2028/9),
 * backslashes, protocol-relative `//host`, credentials in the URL, schemes
 * other than http(s). Origin comparison uses the WHATWG-parsed `origin`
 * (default ports normalized away), never a string prefix — so
 * `https://<erlaubter-host>.evil.example/` cannot pass.
 *
 * Pure function, no DOM access: the caller passes `currentOrigin`.
 */

export type RedirectDecision =
  | { kind: 'internal'; path: string }
  | { kind: 'external'; url: string }
  | { kind: 'fallback'; reason: string }

/**
 * C0 controls (incl. TAB/CR/LF), DEL, LINE/PARAGRAPH SEPARATOR. A browser
 * strips or normalizes these before parsing, so `/<TAB>/evil` turns into
 * `//evil` — the check must run BEFORE the internal-path decision.
 * Code-point comparison on purpose: no escape sequences to get wrong.
 */
function hasUnsafeChars(s: string): boolean {
  for (const ch of s) {
    const c = ch.codePointAt(0) ?? 0
    if (c <= 0x1f || c === 0x7f || c === 0x2028 || c === 0x2029) return true
  }
  return false
}

/** Canonical origin of an allowlist entry, or null if it is not a clean origin. */
export function normalizeOrigin(entry: string): string | null {
  try {
    const u = new URL(entry.trim())
    if (u.username || u.password) return null
    if (u.protocol !== 'https:' && u.protocol !== 'http:') return null
    return u.origin
  } catch {
    return null
  }
}

export function resolveRedirect(
  next: string | null | undefined,
  allowedOrigins: readonly string[],
  currentOrigin: string | null,
): RedirectDecision {
  if (next == null) return { kind: 'fallback', reason: 'empty' }
  const s = String(next).trim()
  if (!s) return { kind: 'fallback', reason: 'empty' }
  if (hasUnsafeChars(s) || s.includes('\\')) {
    return { kind: 'fallback', reason: 'unsafe_chars' }
  }
  if (s.startsWith('/')) {
    if (s.startsWith('//')) return { kind: 'fallback', reason: 'protocol_relative' }
    return { kind: 'internal', path: s }
  }
  let u: URL
  try {
    u = new URL(s) // no base on purpose: relative junk must not resolve
  } catch {
    return { kind: 'fallback', reason: 'unparseable' }
  }
  if (u.protocol !== 'https:' && u.protocol !== 'http:') {
    return { kind: 'fallback', reason: 'scheme' }
  }
  if (u.username || u.password) return { kind: 'fallback', reason: 'credentials' }

  const allowed = new Set<string>()
  for (const entry of allowedOrigins) {
    const o = normalizeOrigin(entry)
    if (o) allowed.add(o)
  }
  const self = currentOrigin ? normalizeOrigin(currentOrigin) : null
  if (self) allowed.add(self)

  if (!allowed.has(u.origin)) return { kind: 'fallback', reason: 'origin_not_allowed' }
  if (self && u.origin === self) {
    return { kind: 'internal', path: u.pathname + u.search + u.hash }
  }
  return { kind: 'external', url: u.href }
}
